'use client'

import { useRef } from 'react'

import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { SSEConnectionOptions } from '@marketplace-web/messaging/sse-connection-data'

import SSEConnectionManager, { EventHandler } from '../SSEConnectionManager'
import { getMessagingSSEURL } from '../utils/url'

export const useMessagingSSEConnection = () => {
  const sseManager = useRef<SSEConnectionManager>(SSEConnectionManager.getInstance())
  const shouldEnableSSEConnection = useAbTest('sse_in_conversation')?.variant === 'on'
  const shouldTestSseConnectionLoad = useFeatureSwitch('sse_in_conversations_test')

  const enableMessagingSSE = () => {
    if (!shouldEnableSSEConnection && !shouldTestSseConnectionLoad) return

    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      platform: 'web',
    }

    const options: SSEConnectionOptions = {
      url: getMessagingSSEURL(),
      headers,
    }
    sseManager.current.enable(options)
  }

  const subscribeToMessageUpdates = (handler: EventHandler) => {
    if (!shouldEnableSSEConnection) return

    sseManager.current.subscribe('message', handler)
  }

  const unsubscribeFromMessageUpdates = (handler: EventHandler) => {
    if (!shouldEnableSSEConnection) return

    sseManager.current.unsubscribe('message', handler)
  }

  const disableMessagingSSE = () => {
    if (!shouldEnableSSEConnection && !shouldTestSseConnectionLoad) return

    sseManager.current.disable()
  }

  return {
    enableMessagingSSE,
    disableMessagingSSE,
    subscribeToMessageUpdates,
    unsubscribeFromMessageUpdates,
  }
}
